import { z } from "zod";

export const WorkflowDbFieldTypeSchema = z.enum([
	"string",
	"number",
	"boolean",
	"object",
	"array",
	"enum",
	"file",
]);

export type WorkflowDbFieldType = z.infer<typeof WorkflowDbFieldTypeSchema>;

export const WorkflowDbArrayItemTypeSchema = z.enum(["string", "number", "boolean", "enum"]);

export type WorkflowDbArrayItemType = z.infer<typeof WorkflowDbArrayItemTypeSchema>;

export type WorkflowDbFieldSchema = {
	key: string;
	type: WorkflowDbFieldType;
	required: boolean;
	enumOptions?: string[];
	arrayItemType?: WorkflowDbArrayItemType;
	properties?: WorkflowDbFieldSchema[];
	arrayItemProperties?: WorkflowDbFieldSchema[];
};

export const WorkflowDbFieldSchemaSchema: z.ZodType<WorkflowDbFieldSchema> = z.lazy(() =>
	z.object({
		key: z.string().min(1),
		type: WorkflowDbFieldTypeSchema,
		required: z.boolean().default(false),
		enumOptions: z.array(z.string().min(1)).optional(),
		arrayItemType: WorkflowDbArrayItemTypeSchema.optional(),
		properties: z.array(WorkflowDbFieldSchemaSchema).optional(),
		arrayItemProperties: z.array(WorkflowDbFieldSchemaSchema).optional(),
	}),
);

export const WorkflowDbSchemaSchema = z.object({
	type: z.literal("object"),
	properties: z.array(WorkflowDbFieldSchemaSchema),
});

export type WorkflowDbSchema = z.infer<typeof WorkflowDbSchemaSchema>;

export const WorkflowDbRecordSchema = z.record(z.string(), z.unknown());
export type WorkflowDbRecord = z.infer<typeof WorkflowDbRecordSchema>;
export const WorkflowDbTableDataSchema = z.record(z.string(), z.array(WorkflowDbRecordSchema));
export type WorkflowDbTableData = z.infer<typeof WorkflowDbTableDataSchema>;

export const WorkflowDbDataResponseSchema = z.object({
	data: z.unknown(),
});

export const WorkflowDbSchemaResponseSchema = z.object({
	schema: z.unknown(),
	enums: z.record(z.string(), z.array(z.unknown())).optional(),
});

export const UpdateWorkflowDbSchemaRequestSchema = z.object({
	schema: WorkflowDbSchemaSchema,
	enums: z.record(z.string(), z.array(z.unknown())).optional(),
});

export const UpdateWorkflowDbSchemaResponseSchema = z.object({
	success: z.boolean(),
	schema: z.unknown(),
	enums: z.record(z.string(), z.array(z.unknown())).optional(),
	index: z.record(z.string(), z.unknown()).optional(),
});

export const ReadRecordRequestSchema = z.object({
	ref: z.string().min(1),
	select: z.array(z.string().min(1)).optional(),
});

export const ReadRecordResponseSchema = z.object({
	rows: z.array(z.record(z.string(), z.unknown())),
});

export const WriteRecordRequestSchema = z.object({
	table: z.string().min(1),
	data: z.record(z.string(), z.unknown()),
});

export const WriteRecordResponseSchema = z.object({
	id: z.string(),
});

export const DeleteRecordRequestSchema = z.object({
	table: z.string().min(1),
	ref: z.string().min(1),
});

export const DeleteRecordResponseSchema = z.object({
	deletedCount: z.number(),
});

export const UpdateRecordRequestSchema = z.object({
	sets: z.array(
		z.object({
			path: z.string().min(1),
			value: z.unknown(),
		}),
	),
});

export const UpdateRecordResponseSchema = z.object({
	updatedCount: z.number(),
});

export type WorkflowDbDataResponse = z.infer<typeof WorkflowDbDataResponseSchema>;
export type WorkflowDbSchemaResponse = z.infer<typeof WorkflowDbSchemaResponseSchema>;
export type UpdateWorkflowDbSchemaRequest = z.infer<typeof UpdateWorkflowDbSchemaRequestSchema>;
export type UpdateWorkflowDbSchemaResponse = z.infer<typeof UpdateWorkflowDbSchemaResponseSchema>;
export type WriteRecordRequest = z.infer<typeof WriteRecordRequestSchema>;
export type WriteRecordResponse = z.infer<typeof WriteRecordResponseSchema>;
export type DeleteRecordRequest = z.infer<typeof DeleteRecordRequestSchema>;
export type DeleteRecordResponse = z.infer<typeof DeleteRecordResponseSchema>;
export type UpdateRecordRequest = z.infer<typeof UpdateRecordRequestSchema>;
export type UpdateRecordResponse = z.infer<typeof UpdateRecordResponseSchema>;
export type ReadRecordRequest = z.infer<typeof ReadRecordRequestSchema>;
export type ReadRecordResponse = z.infer<typeof ReadRecordResponseSchema>;
